type Theme = 'light' | 'dark' | 'auto';
type ThemeListener = (theme: Theme) => void;

class ThemeService {
  private theme: Theme = 'auto';
  private listeners: Set<ThemeListener> = new Set();
  private readonly STORAGE_KEY = 'theme_preference';

  constructor() {
    this.loadTheme();
    this.applyTheme();
    this.watchSystemPreference();
  }

  private loadTheme(): void {
    const stored = localStorage.getItem(this.STORAGE_KEY) as Theme | null;
    if (stored && ['light', 'dark', 'auto'].includes(stored)) {
      this.theme = stored;
    }
  }

  private saveTheme(): void {
    localStorage.setItem(this.STORAGE_KEY, this.theme);
  }

  private applyTheme(): void {
    const root = document.documentElement;

    if (this.theme === 'auto') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', this.theme);
    }

    this.notify();
  }

  private watchSystemPreference(): void {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', () => {
      if (this.theme === 'auto') {
        this.notify();
      }
    });
  }

  getTheme(): Theme {
    return this.theme;
  }

  getEffectiveTheme(): 'light' | 'dark' {
    if (this.theme !== 'auto') {
      return this.theme;
    }

    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  }

  setTheme(theme: Theme): void {
    this.theme = theme;
    this.saveTheme();
    this.applyTheme();
  }

  toggle(): void {
    const effective = this.getEffectiveTheme();
    this.setTheme(effective === 'dark' ? 'light' : 'dark');
  }

  subscribe(listener: ThemeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach(listener => listener(this.theme));
  }
}

export const theme = new ThemeService();
